import { ReactElement } from 'react'
import { Grid, Divider } from '@mui/material'
import { Form } from '@ui/forms'
import { Box } from '@ui/structure'
import GeneralFields from './GeneralFields'
import LayerList from './LayerList'
import { assemblySchema } from './schema'

interface Props {
  id: string
  defaultValues: Assembly
  onSubmit: (values: Assembly) => void
  earthMassForce: number
  allowedUsageTypes?: AssemblyTypes[]
}

const isTimberFrameLayer = (layer: Layer): layer is TimberFrameLayer =>
  layer.kind === 'timber-frame-layer' ||
  layer.kind === 'timber-frame-layer-wall' ||
  layer.kind === 'timber-frame-layer-roof-ceiling'

const sanitizeAssemblyPayload = (assembly: Assembly): Assembly => {
  const isRoofAssembly = assembly.usage_type === 'Roof'
  const isCeilingAssembly = assembly.usage_type === 'Ceiling'

  let expectedTimberKind: AssemblyKind = 'timber-frame-assembly-wall'
  if (isRoofAssembly) {
    expectedTimberKind = 'timber-frame-assembly-roof'
  } else if (isCeilingAssembly) {
    expectedTimberKind = 'timber-frame-assembly-ceiling'
  }
  const initialKind: AssemblyKind =
    assembly.kind === 'timber-frame-assembly' ? expectedTimberKind : assembly.kind

  const cltAssemblyKinds: AssemblyKind[] = [
    'clt-assembly',
    'clt-assembly-roof',
    'clt-assembly-ceiling',
  ]
  const timberAssemblyKinds: AssemblyKind[] = [
    'timber-frame-assembly-wall',
    'timber-frame-assembly-roof',
    'timber-frame-assembly-ceiling',
  ]

  let sanitizedKind: AssemblyKind = initialKind

  if (cltAssemblyKinds.includes(initialKind)) {
    let expectedCltKind: AssemblyKind = 'clt-assembly'
    if (isRoofAssembly) {
      expectedCltKind = 'clt-assembly-roof'
    } else if (isCeilingAssembly) {
      expectedCltKind = 'clt-assembly-ceiling'
    }
    sanitizedKind = expectedCltKind
  } else if (timberAssemblyKinds.includes(initialKind)) {
    sanitizedKind = expectedTimberKind
  }

  const sanitizedLayers = assembly.layers.map(layer => {
    if (!isTimberFrameLayer(layer)) return layer

    const normalizedLayer: TimberFrameLayer = {
      ...layer,
      insulation: layer.insulation ?? null,
      insulation_thickness: layer.insulation_thickness ?? null,
    }

    if (
      !(isRoofAssembly || isCeilingAssembly) &&
      normalizedLayer.kind !== 'timber-frame-layer-roof-ceiling'
    ) {
      return {
        ...normalizedLayer,
        kind: 'timber-frame-layer-wall',
      }
    }

    if (
      (isRoofAssembly || isCeilingAssembly) &&
      (normalizedLayer.kind === 'timber-frame-layer-roof-ceiling' ||
        'standard_beam_cs' in normalizedLayer)
    ) {
      return {
        ...normalizedLayer,
        kind: 'timber-frame-layer-roof-ceiling',
      }
    }

    return normalizedLayer
  }) as Layer[]

  const { vibration_check_settings, rips_held_by_plate, ...rest } = assembly

  const sanitizedAssembly: Assembly = {
    ...rest,
    kind: sanitizedKind,
    layers: sanitizedLayers,
  }

  if (sanitizedKind === 'timber-frame-assembly-wall') {
    sanitizedAssembly.rips_held_by_plate = rips_held_by_plate ?? true
  }

  if (isCeilingAssembly && vibration_check_settings) {
    const vibrationSettings = vibration_check_settings as VibrationCheckSettings
    sanitizedAssembly.vibration_check_settings = vibrationSettings
  }

  return sanitizedAssembly
}

const AssemblyForm = ({
  defaultValues,
  id,
  onSubmit,
  earthMassForce,
  allowedUsageTypes,
}: Props): ReactElement => {
  return (
    <Form
      id={id}
      onSubmit={values => onSubmit(sanitizeAssemblyPayload(values as Assembly))}
      validationSchema={assemblySchema}
      defaultValues={{
        ...assemblySchema.getDefault(),
        ...defaultValues,
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={3} display="flex">
          <Box pr={2} pb={2}>
            <GeneralFields earthMassForce={earthMassForce} allowedUsageTypes={allowedUsageTypes} />
          </Box>
          <Divider orientation="vertical" flexItem />
        </Grid>
        <Grid item xs={9}>
          <LayerList earthMassForce={earthMassForce} />
        </Grid>
      </Grid>
    </Form>
  )
}

export default AssemblyForm
